import Ionicons from "@expo/vector-icons/Ionicons";
import {
  DateTimePickerAndroid,
  type DateTimePickerChangeEvent,
} from "@react-native-community/datetimepicker";
import { createElement, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { IOSPickerSheet } from "@/components/ui/ios-picker-sheet";
import i18n from "@/i18n";

/** "HH:MM" (24h) → a Date today at that local time, defaulting to 8:00 PM. */
function parseTime(value: string): Date {
  const d = new Date();
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  d.setHours(m ? Number(m[1]) : 20, m ? Number(m[2]) : 0, 0, 0);
  return d;
}

/** A Date → local "HH:MM" (24h) for storage. */
function toTimeString(date: Date): string {
  const h = `${date.getHours()}`.padStart(2, "0");
  const m = `${date.getMinutes()}`.padStart(2, "0");
  return `${h}:${m}`;
}

/** "HH:MM" (24h) → a localized display label (e.g. "8:00 PM"). */
export function formatReminderTime(value: string): string {
  return parseTime(value).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Compact time-of-day field for the custom reflection reminder: a chip that
 * opens the native time picker (or a web time input). `value` is a 24h "HH:MM"
 * string.
 */
export function ReminderTimePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const c = useTheme();
  const [showIOS, setShowIOS] = useState(false);
  const selected = parseTime(value);

  function onNativeValueChange(_event: DateTimePickerChangeEvent, next: Date) {
    onChange(toTimeString(next));
  }

  function openPicker() {
    if (Platform.OS === "android") {
      DateTimePickerAndroid.open({
        value: selected,
        mode: "time",
        display: "default",
        onValueChange: onNativeValueChange,
        onDismiss: () => {},
      });
      return;
    }
    setShowIOS(true);
  }

  return (
    <View style={styles.root}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={i18n.t("growth.reminderTime.changeA11y")}
        onPress={Platform.OS === "web" ? undefined : openPicker}
        style={[
          styles.chip,
          { borderColor: c.tint, backgroundColor: c.backgroundSelected },
        ]}
      >
        <Ionicons name="time-outline" size={15} color={c.tint} />
        {Platform.OS === "web" ? (
          createElement("input", {
            type: "time",
            value,
            "aria-label": i18n.t("growth.reminderTime.inputA11y"),
            onChange: (e: { currentTarget: { value: string } }) => {
              if (e.currentTarget.value) onChange(e.currentTarget.value);
            },
            style: StyleSheet.flatten([styles.webInput, { color: c.text }]),
          })
        ) : (
          <ThemedText variant="caption" tone="tint">
            {i18n.t("growth.reminderTime.at", {
              time: formatReminderTime(value),
            })}
          </ThemedText>
        )}
      </Pressable>

      {/* The shared sheet, like every other wheel in the app. Inline it had no
          Done and no dismissal — see `ios-picker-sheet.tsx`. */}
      {Platform.OS === "ios" ? (
        <IOSPickerSheet
          visible={showIOS}
          value={selected}
          mode="time"
          onValueChange={onNativeValueChange}
          onClose={() => setShowIOS(false)}
          accessibilityLabel={i18n.t("growth.reminderTime.inputA11y")}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { alignSelf: "flex-start", gap: Spacing.xs },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  webInput: {
    borderWidth: 0,
    backgroundColor: "transparent",
    fontSize: 13,
  },
});
